import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Calculator,
  Globe,
  Users,
  Zap,
  Video,
  Headphones,
  Target,
  Lightbulb,
  Sparkles,
  Check,
  Play,
  DollarSign,
  TrendingUp,
  Phone,
  Star
} from 'lucide-react';

const Services: React.FC = () => {
  const handleGetStartedClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="relative min-h-screen bg-slate-50 overflow-hidden">
      {/* Hero Section */}
      <section className="pt-28 pb-12 relative bg-white">
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-[1.1] tracking-tight">
            Scale Your Business
            <span className="block mt-2 bg-gradient-to-r from-slate-700 to-slate-500 bg-clip-text text-transparent">
              With Expert Help
            </span>
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            Everything you need to grow, from marketing to operations.
            Built by insurance professionals, for insurance professionals.
          </p>
          
          <Link
            to="/services/contact"
            onClick={handleGetStartedClick}
            className="group inline-flex items-center gap-3 px-8 py-4 bg-slate-900 text-white font-semibold rounded-full hover:bg-slate-800 transition-all duration-300 shadow-lg shadow-slate-900/20 active:scale-95"
          >
            Get Started
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto relative">
          {/* Connecting lines - positioned behind cards */}
          <div className="absolute inset-0 pointer-events-none hidden md:block" style={{ zIndex: 0 }}>
            {/* Vertical lines */}
            <div className="absolute left-[25%] top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-slate-400/50 to-transparent" />
            <div className="absolute left-[50%] top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-slate-400/50 to-transparent" />
            <div className="absolute left-[75%] top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-slate-400/50 to-transparent" />
            
            {/* Horizontal lines - positioned at row gaps */}
            <div className="absolute top-[360px] left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/50 to-transparent" />
            <div className="absolute top-[570px] left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/50 to-transparent" />
            <div className="absolute top-[780px] left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/50 to-transparent" />
            <div className="absolute top-[990px] left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/50 to-transparent" />
          </div>
          
          <div className="relative" style={{ zIndex: 1 }}>
          
          {/* Row 1 */}
          <div className="grid grid-cols-12 gap-4 lg:gap-5 mb-4 lg:mb-5">
            {/* Enterprise Dialer - Featured */}
            <div className="col-span-12 lg:col-span-8 relative group">
              <div className="relative h-full bg-slate-900/95 backdrop-blur-sm rounded-3xl p-8 lg:p-10 overflow-hidden min-h-[340px] border border-slate-700/50 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_0_1px_rgba(51,65,85,0.5)]">
                {/* Imprint icons */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/4 opacity-[0.06]">
                  <Headphones className="w-72 h-72 text-white" strokeWidth={1} />
                </div>
                <div className="absolute right-48 top-1/3 -translate-y-1/2 opacity-[0.05]">
                  <Phone className="w-40 h-40 text-white" strokeWidth={1} />
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-white/10 text-white/90 text-xs font-medium rounded-full">
                      Teams 20+
                    </span>
                    <span className="px-3 py-1 bg-amber-400 text-slate-900 text-xs font-bold rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> Top Seller
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mb-3">
                    <h3 className="text-3xl lg:text-4xl font-bold text-white">Dialer System</h3>
                    {/* Sound wave animation */}
                    <div className="hidden sm:flex items-center gap-1 opacity-50">
                      {[...Array(5)].map((_, i) => (
                        <div 
                          key={i} 
                          className="w-1 bg-gradient-to-t from-blue-400 to-cyan-400 rounded-full animate-soundwave"
                          style={{ 
                            height: `${14 + (i % 3) * 8}px`,
                            animationDelay: `${i * 0.15}s`
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                  <p className="text-lg text-slate-300 max-w-md leading-relaxed mb-6">
                    Enterprise-grade power dialer for teams of 20+ reps. Triple your connect rates.
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {['Multi-line dialing', 'Call recording', 'Real-time analytics', 'CRM sync'].map((feature, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-white/10 text-white text-xs rounded-full">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tax & Bookkeeping */}
            <div className="col-span-12 sm:col-span-6 lg:col-span-4 group">
              <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-3xl p-6 lg:p-8 border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 overflow-hidden min-h-[320px]">
                {/* Imprint icon - bottom right */}
                <div className="absolute -right-6 -bottom-6 opacity-[0.04]">
                  <Calculator className="w-40 h-40 text-slate-900" strokeWidth={1} />
                </div>
                
                <div className="relative">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Tax & Bookkeeping</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Professional tax preparation and bookkeeping for insurance professionals.
                  </p>
                  
                  <div className="space-y-2">
                    {['Quarterly planning', 'Commission tracking', 'Expense management'].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Bookkeeping UI - bottom left */}
                <div className="absolute bottom-4 left-6 w-36">
                  {/* Mini spreadsheet */}
                  <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                    {/* Header row */}
                    <div className="flex text-[8px] font-medium text-slate-500 bg-slate-100 border-b border-slate-200">
                      <div className="w-12 px-1.5 py-1 border-r border-slate-200">Item</div>
                      <div className="flex-1 px-1.5 py-1 text-right">Amount</div>
                    </div>
                    {/* Data rows */}
                    <div className="flex text-[8px] text-slate-600 border-b border-slate-100">
                      <div className="w-12 px-1.5 py-1 border-r border-slate-100">Sales</div>
                      <div className="flex-1 px-1.5 py-1 text-right text-emerald-600">+$8,240</div>
                    </div>
                    <div className="flex text-[8px] text-slate-600 border-b border-slate-100">
                      <div className="w-12 px-1.5 py-1 border-r border-slate-100">Comm.</div>
                      <div className="flex-1 px-1.5 py-1 text-right text-emerald-600">+$4,210</div>
                    </div>
                    <div className="flex text-[8px] text-slate-600">
                      <div className="w-12 px-1.5 py-1 border-r border-slate-100">Expense</div>
                      <div className="flex-1 px-1.5 py-1 text-right text-red-500">-$1,850</div>
                    </div>
                    {/* Total row */}
                    <div className="flex text-[9px] font-bold bg-emerald-50 border-t border-emerald-100">
                      <div className="w-12 px-1.5 py-1.5 border-r border-emerald-100 text-slate-700">Total</div>
                      <div className="flex-1 px-1.5 py-1.5 text-right text-emerald-600">$12,450</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-12 gap-4 lg:gap-5 mb-4 lg:mb-5">
            {/* Website Development */}
            <div className="col-span-12 lg:col-span-5 group">
              <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-3xl p-6 lg:p-8 border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 overflow-hidden min-h-[320px]">
                {/* Imprint icon */}
                <div className="absolute -right-8 -top-8 opacity-[0.04]">
                  <Globe className="w-44 h-44 text-slate-900" strokeWidth={1} />
                </div>
                
                <div className="relative">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Website Development</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Professional websites that convert visitors into clients.
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {['Responsive', 'SEO', 'Fast', 'Secure'].map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Code snippets floating around */}
                <div className="absolute bottom-24 right-4 px-2 py-1 bg-slate-900 rounded text-[10px] font-mono text-green-400 opacity-70">
                  &lt;div class="hero"&gt;
                </div>
                <div className="absolute bottom-16 right-8 px-2 py-1 bg-slate-900 rounded text-[10px] font-mono text-blue-400 opacity-60">
                  .container {'{'}
                </div>
                <div className="absolute bottom-8 right-2 px-2 py-1 bg-slate-900 rounded text-[10px] font-mono text-purple-400 opacity-50">
                  npm run build
                </div>
                
                {/* Browser mockup - bottom left */}
                <div className="absolute bottom-4 left-4 w-40 h-28 bg-slate-800 rounded-xl shadow-2xl overflow-hidden border border-slate-700">
                  {/* Browser header */}
                  <div className="h-5 bg-slate-700 flex items-center px-2 gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-400"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    <div className="flex-1 mx-2 h-2.5 bg-slate-600 rounded-full flex items-center px-1.5">
                      <div className="w-1 h-1 bg-slate-500 rounded-full"></div>
                    </div>
                  </div>
                  {/* Website content */}
                  <div className="p-2 bg-gradient-to-b from-slate-800 to-slate-900">
                    {/* Nav */}
                    <div className="flex gap-1 mb-2">
                      <div className="w-4 h-1.5 bg-blue-400 rounded"></div>
                      <div className="flex-1"></div>
                      <div className="w-2 h-1.5 bg-slate-600 rounded"></div>
                      <div className="w-2 h-1.5 bg-slate-600 rounded"></div>
                      <div className="w-2 h-1.5 bg-slate-600 rounded"></div>
                    </div>
                    {/* Hero section */}
                    <div className="flex gap-2">
                      <div className="flex-1 space-y-1">
                        <div className="h-2 bg-white rounded w-4/5"></div>
                        <div className="h-1 bg-slate-500 rounded w-full"></div>
                        <div className="h-1 bg-slate-500 rounded w-3/4"></div>
                        <div className="h-3 w-8 bg-blue-500 rounded mt-1.5"></div>
                      </div>
                      <div className="w-10 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded"></div>
                    </div>
                  </div>
                </div>
                
                {/* Cursor pointer */}
                <div className="absolute bottom-14 left-36">
                  <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
                    <path d="M2 2L2 18L6 14L10 22L13 20L9 12L15 12L2 2Z" fill="#1e293b" stroke="white" strokeWidth="1.5"/>
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Social Media */}
            <div className="col-span-12 sm:col-span-6 lg:col-span-4 group">
              <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-3xl p-6 lg:p-8 border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 overflow-hidden">
                {/* Social icons - static, clean layout */}
                <div className="absolute top-6 right-6 flex gap-2">
                  {/* Instagram */}
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  {/* Facebook */}
                  <div className="w-9 h-9 rounded-xl bg-[#1877F2] flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  {/* TikTok */}
                  <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                  </div>
                  {/* LinkedIn */}
                  <div className="w-9 h-9 rounded-xl bg-[#0A66C2] flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </div>
                </div>
                
                {/* Engagement stats */}
                <div className="absolute bottom-6 right-6 flex items-center gap-2">
                  <div className="flex -space-x-1.5">
                    <div className="w-5 h-5 rounded-full bg-slate-300 border-2 border-white"></div>
                    <div className="w-5 h-5 rounded-full bg-slate-400 border-2 border-white"></div>
                    <div className="w-5 h-5 rounded-full bg-slate-500 border-2 border-white"></div>
                  </div>
                  <span className="text-xs text-slate-500">+2.4k</span>
                </div>
                
                <div className="relative">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Social Media</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Build your brand with consistent, engaging content.
                  </p>
                  
                  <div className="space-y-2">
                    {['Content creation', 'Engagement strategy', 'Analytics'].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-slate-500" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Recruiting */}
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 group">
              <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-3xl p-6 lg:p-8 border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 overflow-hidden min-h-[320px]">
                {/* Imprint icon */}
                <div className="absolute -right-6 -bottom-6 opacity-[0.04]">
                  <Users className="w-36 h-36 text-slate-900" strokeWidth={1} />
                </div>
                
                <div className="relative">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Recruiting</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Attract top talent with proven funnels.
                  </p>
                  
                  {/* Process steps */}
                  <div className="flex items-center gap-1 mt-4">
                    {['Ad', 'Land', 'Screen', 'Hire'].map((step, idx) => (
                      <React.Fragment key={idx}>
                        <div className="px-2 py-1 bg-slate-100 text-xs text-slate-600 rounded">{step}</div>
                        {idx < 3 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                
                {/* Recruiting funnel - bottom left */}
                <div className="absolute bottom-3 left-4 w-28">
                  <svg viewBox="0 0 100 95" className="w-full">
                    <defs>
                      {/* Gradient for funnel body */}
                      <linearGradient id="funnelGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#e2e8f0"/>
                        <stop offset="50%" stopColor="#f8fafc"/>
                        <stop offset="100%" stopColor="#cbd5e1"/>
                      </linearGradient>
                      {/* Inner gradient */}
                      <linearGradient id="funnelInner" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.9"/>
                        <stop offset="100%" stopColor="#0f172a" stopOpacity="0.95"/>
                      </linearGradient>
                      {/* Highlight */}
                      <linearGradient id="funnelHighlight" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="0"/>
                        <stop offset="30%" stopColor="#ffffff" stopOpacity="0.4"/>
                        <stop offset="70%" stopColor="#ffffff" stopOpacity="0"/>
                      </linearGradient>
                    </defs>
                    
                    {/* Funnel outer body - 3D effect */}
                    <path d="M8 15 Q8 10 15 10 L85 10 Q92 10 92 15 L72 50 L72 72 Q72 76 68 76 L32 76 Q28 76 28 72 L28 50 Z" fill="url(#funnelGradient)" stroke="#94a3b8" strokeWidth="1.5"/>
                    
                    {/* Funnel inner (dark opening) */}
                    <path d="M18 16 L82 16 L65 48 L65 70 L35 70 L35 48 Z" fill="url(#funnelInner)"/>
                    
                    {/* Rim highlight - 3D effect */}
                    <ellipse cx="50" cy="13" rx="38" ry="5" fill="url(#funnelHighlight)"/>
                    <path d="M12 13 Q50 8 88 13" fill="none" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.5"/>
                    
                    {/* People icons dropping in */}
                    <g className="animate-float-subtle">
                      <circle cx="28" cy="3" r="4" fill="#94a3b8"/>
                      <ellipse cx="28" cy="9" rx="3" ry="1.5" fill="#94a3b8"/>
                    </g>
                    <g className="animate-float-subtle" style={{ animationDelay: '0.3s' }}>
                      <circle cx="50" cy="0" r="4.5" fill="#64748b"/>
                      <ellipse cx="50" cy="7" rx="3.5" ry="1.5" fill="#64748b"/>
                    </g>
                    <g className="animate-float-subtle" style={{ animationDelay: '0.6s' }}>
                      <circle cx="72" cy="4" r="4" fill="#475569"/>
                      <ellipse cx="72" cy="10" rx="3" ry="1.5" fill="#475569"/>
                    </g>
                    
                    {/* Filtering lines inside funnel */}
                    <line x1="25" y1="30" x2="75" y2="30" stroke="#334155" strokeWidth="1" strokeDasharray="4,3" strokeOpacity="0.5"/>
                    <line x1="30" y1="42" x2="70" y2="42" stroke="#334155" strokeWidth="1" strokeDasharray="4,3" strokeOpacity="0.5"/>
                    <line x1="35" y1="54" x2="65" y2="54" stroke="#334155" strokeWidth="1" strokeDasharray="4,3" strokeOpacity="0.4"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-12 gap-4 lg:gap-5 mb-4 lg:mb-5">
            {/* Automations */}
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 group">
              <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-3xl p-6 lg:p-8 border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 overflow-hidden">
                {/* 3D Lightning bolt */}
                <div className="absolute -right-4 -bottom-4">
                  <svg width="120" height="140" viewBox="0 0 24 26" className="drop-shadow-xl">
                    {/* Shadow layer */}
                    <polygon 
                      points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" 
                      fill="#1e3a5f"
                      transform="translate(1.5, 2)"
                      opacity="0.3"
                    />
                    {/* Main bolt */}
                    <polygon 
                      points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" 
                      fill="url(#navyGradient)"
                    />
                    {/* Highlight */}
                    <polygon 
                      points="13 2 8 8 12 8 12 14 9 14 13 2" 
                      fill="#2563eb"
                      opacity="0.4"
                    />
                    <defs>
                      <linearGradient id="navyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#1e40af" />
                        <stop offset="100%" stopColor="#1e3a8a" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                
                {/* Single gear - top right */}
                <div className="absolute top-4 right-4">
                  <svg viewBox="0 0 60 60" className="w-16 h-16">
                    <g transform="translate(30, 30)">
                      <circle cx="0" cy="0" r="22" fill="none" stroke="#cbd5e1" strokeWidth="3"/>
                      <circle cx="0" cy="0" r="8" fill="#cbd5e1"/>
                      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                        <rect 
                          key={i}
                          x="-3.5" 
                          y="-30" 
                          width="7" 
                          height="10" 
                          rx="2"
                          fill="#cbd5e1"
                          transform={`rotate(${angle})`}
                        />
                      ))}
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 0 0"
                        to="360 0 0"
                        dur="10s"
                        repeatCount="indefinite"
                      />
                    </g>
                  </svg>
                </div>
                
                <div className="relative">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Automations</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Custom workflows that eliminate repetitive tasks.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Lead Generation - Wide */}
            <div className="col-span-12 lg:col-span-6 group">
              <div className="relative h-full bg-slate-900/95 backdrop-blur-sm rounded-3xl p-8 lg:p-10 overflow-hidden border border-slate-700/50 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_0_1px_rgba(51,65,85,0.5)]">
                {/* Imprint icon */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/4 opacity-[0.06]">
                  <Target className="w-60 h-60 text-white" strokeWidth={1} />
                </div>
                
                {/* Rising chart - white bars, positioned left of imprint */}
                <div className="absolute bottom-6 right-32 flex items-end gap-1.5 opacity-50">
                  <div className="w-2.5 h-5 bg-white/30 rounded-t"></div>
                  <div className="w-2.5 h-8 bg-white/40 rounded-t"></div>
                  <div className="w-2.5 h-12 bg-white/50 rounded-t"></div>
                  <div className="w-2.5 h-16 bg-white/70 rounded-t"></div>
                  <TrendingUp className="w-4 h-4 text-white/60 ml-1" />
                </div>
                
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-white mb-2">Lead Generation</h3>
                  <p className="text-slate-300 max-w-sm mb-4">
                    Fill your pipeline with qualified prospects through multi-channel campaigns.
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {['Multi-channel', 'Qualified leads', 'ROI tracking'].map((tag, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-white/10 text-white text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Content & Video */}
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 group">
              <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-3xl p-6 lg:p-8 border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 overflow-hidden min-h-[200px]">
                {/* Imprint icon */}
                <div className="absolute -right-6 -top-6 opacity-[0.04]">
                  <Video className="w-36 h-36 text-slate-900" strokeWidth={1} />
                </div>
                
                <div className="relative">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Content & Video</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Professional video production that builds trust.
                  </p>
                </div>
                
                {/* Video player mockup - bottom left */}
                <div className="absolute bottom-4 left-4 w-28 h-20 rounded-xl overflow-hidden shadow-2xl border border-slate-200">
                  {/* Video thumbnail */}
                  <div className="w-full h-full relative bg-gradient-to-br from-slate-100 via-slate-50 to-white">
                    {/* Film strip decoration */}
                    <div className="absolute left-0 top-0 bottom-0 w-3 bg-slate-800 flex flex-col justify-around py-1">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-1.5 h-1.5 bg-slate-600 mx-auto rounded-sm"></div>
                      ))}
                    </div>
                    {/* Play button */}
                    <div className="absolute inset-0 flex items-center justify-center pl-2">
                      <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                        <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="absolute bottom-0 left-3 right-0 h-1.5 bg-slate-200">
                      <div className="h-full w-1/3 bg-red-500 rounded-r"></div>
                      <div className="absolute top-1/2 -translate-y-1/2 left-1/3 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white shadow"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-12 gap-4 lg:gap-5">
            {/* Sales Consulting */}
            <div className="col-span-12 lg:col-span-7 group">
              <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.2)] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_0_0_1px_rgba(148,163,184,0.3),0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 overflow-hidden">
                {/* Imprint icon */}
                <div className="absolute -right-10 -bottom-10 opacity-[0.04]">
                  <Lightbulb className="w-56 h-56 text-slate-900" strokeWidth={1} />
                </div>
                
                {/* 1:1 badge - top right */}
                <div className="hidden lg:flex absolute top-8 right-10 flex-col items-center gap-1">
                  <div className="text-5xl font-bold text-blue-800">1:1</div>
                  <div className="text-sm text-slate-500 text-center">Personal<br/>Coaching</div>
                </div>
                
                <div className="relative flex flex-col gap-6">
                  <div className="flex-1 lg:pr-32">
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Sales Consulting</h3>
                    <p className="text-slate-600 leading-relaxed mb-5">
                      One-on-one coaching and strategy sessions to refine your sales process.
                    </p>
                    
                    <div className="flex flex-wrap gap-3">
                      {['Script development', 'Objection handling', 'Pipeline review', 'Coaching'].map((item, idx) => (
                        <span key={idx} className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-full">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* CTA Card - Clean, no dots */}
            <div className="col-span-12 lg:col-span-5 group">
              <div className="relative h-full bg-slate-900/95 backdrop-blur-sm rounded-3xl p-8 lg:p-10 overflow-hidden border border-slate-700/50 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_0_1px_rgba(51,65,85,0.5)] flex flex-col justify-between min-h-[280px]">
                {/* Subtle gradient only */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-900"></div>
                
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-white mb-3">Ready to Scale?</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Let's discuss which services will have the biggest impact on your business.
                  </p>
                </div>
                
                <div className="relative z-10 mt-6">
                  <Link
                    to="/services/contact"
                    onClick={handleGetStartedClick}
                    className="group/btn inline-flex items-center justify-center gap-3 w-full sm:w-64 px-8 py-4 bg-white text-slate-900 font-semibold rounded-full hover:bg-slate-100 transition-all duration-300 active:scale-95 active:bg-slate-200"
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 relative overflow-hidden bg-white">
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            Focus on Selling.
            <span className="block text-slate-400">We'll Handle the Rest.</span>
          </h2>
          <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
            The most successful agents know when to delegate. 
            Partner with us and watch your business grow.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/services/contact"
              onClick={handleGetStartedClick}
              className="group px-10 py-5 bg-slate-900 text-white font-bold rounded-full hover:bg-slate-800 transition-all duration-300 shadow-xl shadow-slate-900/20 flex items-center gap-3 text-lg"
            >
              Get Started
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/apply"
              onClick={handleGetStartedClick}
              className="px-10 py-5 bg-slate-100 text-slate-700 font-semibold rounded-full hover:bg-slate-200 transition-all duration-300 text-lg"
            >
              Join Our Team
            </Link>
          </div>
        </div>
      </section>

      {/* Custom animation styles - subtler */}
      <style>{`
        @keyframes float-subtle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        @keyframes soundwave {
          0%, 100% { transform: scaleY(0.6); }
          50% { transform: scaleY(1); }
        }
        @keyframes glow-subtle {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        .animate-float-subtle {
          animation: float-subtle 4s ease-in-out infinite;
        }
        .animate-soundwave {
          animation: soundwave 1.5s ease-in-out infinite;
        }
        .animate-glow-subtle {
          animation: glow-subtle 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Services;
